import { useEffect, useState, useCallback } from "react";
import api from "../api";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import "./moduliProtocollati.css";
import CustomSelect from "../CustomSelect";

const EditModuliProtocollati = () => {
  const [loading, setLoading] = useState(true);
  const [modulo, setModulo] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const [form, setForm] = useState({
    titoloModulo: "",
    oreModulo: "",
    modalitàErogazione: "",
    materialeAttrezzature: "",
    previsteDispense: "",
    proveFinali: "",
    descrizione: "",
  });

  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const canEdit = userRole === "TRAINER";

  const modalitàErogazioneOptions = [
    { value: "Fad", label: "Fad" },
    { value: "Presenza", label: "Presenza" },
    { value: "Entrambi", label: "Entrambi" },
  ];

  const previsteDispenseOptions = [
    { value: "No", label: "No" },
    { value: "Sì, libro", label: "Sì, libro" },
    { value: "Sì, elaborati", label: "Sì, elaborati" },
  ];

  const proveFinaliOptions = [
    { value: "No", label: "No" },
    { value: "Sì, orale", label: "Sì, orale" },
    { value: "Sì, scritta", label: "Sì, scritta" },
  ];

  const getLabel = (options, value) =>
    options.find((opt) => opt.value === value)?.label || value;

  const resolveCodiceFiscale = useCallback(() => {
    return (
      searchParams.get("codiceFiscale") ||
      location.state?.codiceFiscale ||
      location.state?.moduli?.codiceFiscaleTrainer ||
      location.state?.moduli?.trainer?.codiceFiscale ||
      ""
    );
  }, [location.state, searchParams]);

  const fetchModulo = useCallback(async () => {
    setLoading(true);
    try {
      const codiceFiscaleParam = resolveCodiceFiscale();

      if (!codiceFiscaleParam) {
        toast.error(
          "Codice fiscale non disponibile. Impossibile recuperare il modulo.",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          }
        );
        return;
      }

      const response = await api.get("/moduli-protocollati/trainer", {
        params: {
          codiceFiscale: codiceFiscaleParam,
        },
      });

      const dataArray = response.data?.content || response.data || [];
      const foundModulo = dataArray.find(
        (m) => String(m.idModuloTrainer) === String(id)
      );

      if (!foundModulo) {
        toast.error("Modulo protocollato non trovato.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }

      setModulo(foundModulo);
      setForm({
        titoloModulo: foundModulo.titoloModulo || "",
        oreModulo: foundModulo.oreModulo || "",
        modalitàErogazione: foundModulo.modalitàErogazione || "",
        materialeAttrezzature: foundModulo.materialeAttrezzature || "",
        previsteDispense: foundModulo.previsteDispense || "",
        proveFinali: foundModulo.proveFinali || "",
        descrizione: foundModulo.descrizione || "",
      });
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Errore durante il recupero del modulo.",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        }
      );
    } finally {
      setLoading(false);
    }
  }, [id, resolveCodiceFiscale]);

  useEffect(() => {
    const role = localStorage.getItem("userRole");

    if (role !== "TRAINER" && role !== "ADMIN") {
      alert(
        "Accedi come docente o amministratore per visualizzare i moduli protocollati!"
      );
      navigate("/login");
      localStorage.clear();
    } else {
      setUserRole(role);
      fetchModulo();
    }
  }, [navigate, fetchModulo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      switch (name) {
        case "titoloModulo":
        case "modalitàErogazione":
        case "materialeAttrezzature":
        case "previsteDispense":
        case "proveFinali":
        case "descrizione":
          if (value.trim() !== "") delete newErrors[name];
          break;
        case "oreModulo":
          if (value !== "" && !isNaN(value)) delete newErrors[name];
          break;
        default:
          break;
      }
      return newErrors;
    });
  };

  const validate = () => {
    const inputErrors = {};
    if (!form.titoloModulo.trim()) {
      inputErrors.titoloModulo =
        "Il campo Titolo del modulo non può essere vuoto!";
    }
    if (!form.oreModulo || isNaN(form.oreModulo)) {
      inputErrors.oreModulo = "Il campo Ore del modulo non può essere vuoto!";
    }
    if (!form.modalitàErogazione.trim()) {
      inputErrors.modalitàErogazione =
        "Il campo Modalità di erogazione non può essere vuoto!";
    }
    if (!form.materialeAttrezzature.trim()) {
      inputErrors.materialeAttrezzature =
        "Il campo Materiale e attrezzature non può essere vuoto!";
    }
    if (!form.previsteDispense.trim()) {
      inputErrors.previsteDispense =
        "Il campo Dispense previste non può essere vuoto!";
    }
    if (!form.proveFinali.trim()) {
      inputErrors.proveFinali = "Il campo Prove finali non può essere vuoto!";
    }
    setErrors(inputErrors);
    return Object.keys(inputErrors).length === 0;
  };

  const handleSave = async () => {
    if (!canEdit) return;
    if (!validate()) return;
    try {
      setLoading(true);
      const response = await api.put(`/moduli-protocollati/${id}`, {
        ...form,
        oreModulo: parseInt(form.oreModulo),
      });
      if (response.status === 200) {
        toast.success("Modifiche salvate con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setModulo({ ...modulo, ...form });
        setEditMode(false);
      } else {
        toast.error("Errore nel salvataggio!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (modulo) {
      setForm({
        titoloModulo: modulo.titoloModulo || "",
        oreModulo: modulo.oreModulo || "",
        modalitàErogazione: modulo.modalitàErogazione || "",
        materialeAttrezzature: modulo.materialeAttrezzature || "",
        previsteDispense: modulo.previsteDispense || "",
        proveFinali: modulo.proveFinali || "",
        descrizione: modulo.descrizione || "",
      });
    }
    setErrors({});
    setEditMode(false);
  };

  if (loading || !modulo) return <div>Caricamento...</div>;

  return (
    <div className="edit-moduli-modern-bg">
      {loading && (
        <div className="edit-moduli-modern-overlay">
          <div className="edit-moduli-modern-spinner"></div>
        </div>
      )}
      <div className="edit-moduli-modern-card">
        <div className="edit-moduli-modern-header">
          <button
            onClick={() => navigate("/moduli-protocollati")}
            className="edit-moduli-modern-back"
            title="Torna indietro"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <h2>
            <i className="fa-solid fa-file-circle-check"></i>
            Modifica Modulo Protocollato
          </h2>
          {canEdit && !editMode && (
            <button
              className="edit-moduli-modern-edit"
              onClick={() => setEditMode(true)}
            >
              <i className="fa-solid fa-pen"></i>
              Modifica
            </button>
          )}
        </div>
        <form
          className="edit-moduli-modern-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="edit-moduli-modern-fields">
            <div className="edit-moduli-modern-field">
              <label className="edit-moduli-modern-label">
                <i className="fa-solid fa-file-alt"></i>
                Titolo del modulo
              </label>
              {editMode ? (
                <input
                  id="titoloModulo"
                  name="titoloModulo"
                  type="text"
                  value={form.titoloModulo}
                  onChange={handleChange}
                  className={errors.titoloModulo ? "error" : ""}
                  placeholder="Inserisci il titolo del modulo"
                />
              ) : (
                <span className="edit-moduli-modern-text">
                  {modulo.titoloModulo}
                </span>
              )}
              {errors.titoloModulo && (
                <span className="edit-moduli-modern-error">
                  <i className="fa-solid fa-exclamation-triangle"></i>
                  {errors.titoloModulo}
                </span>
              )}
            </div>
            <div className="edit-moduli-modern-field">
              <label className="edit-moduli-modern-label">
                <i className="fa-solid fa-clock"></i>
                Ore del modulo
              </label>
              {editMode ? (
                <input
                  id="oreModulo"
                  name="oreModulo"
                  type="number"
                  value={form.oreModulo}
                  onChange={handleChange}
                  className={errors.oreModulo ? "error" : ""}
                  placeholder="Inserisci le ore del modulo"
                />
              ) : (
                <span className="edit-moduli-modern-text">
                  {modulo.oreModulo}
                </span>
              )}
              {errors.oreModulo && (
                <span className="edit-moduli-modern-error">
                  <i className="fa-solid fa-exclamation-triangle"></i>
                  {errors.oreModulo}
                </span>
              )}
            </div>
            <div className="edit-moduli-modern-field">
              <label className="edit-moduli-modern-label">
                <i className="fa-solid fa-chalkboard-teacher"></i>
                Modalità di erogazione
              </label>
              {!editMode ? (
                <span className="edit-moduli-modern-text">
                  {getLabel(
                    modalitàErogazioneOptions,
                    modulo.modalitàErogazione
                  )}
                </span>
              ) : (
                <CustomSelect
                  options={modalitàErogazioneOptions}
                  value={
                    form.modalitàErogazione
                      ? {
                          value: form.modalitàErogazione,
                          label: getLabel(
                            modalitàErogazioneOptions,
                            form.modalitàErogazione
                          ),
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange({
                      target: {
                        name: "modalitàErogazione",
                        value: selected ? selected.value : "",
                      },
                    })
                  }
                  placeholder="Seleziona modalità di erogazione"
                />
              )}
              {errors.modalitàErogazione && (
                <span className="edit-moduli-modern-error">
                  <i className="fa-solid fa-exclamation-triangle"></i>
                  {errors.modalitàErogazione}
                </span>
              )}
            </div>
            <div className="edit-moduli-modern-field">
              <label className="edit-moduli-modern-label">
                <i className="fa-solid fa-tools"></i>
                Materiale e attrezzature
              </label>
              {editMode ? (
                <input
                  id="materialeAttrezzature"
                  name="materialeAttrezzature"
                  type="text"
                  value={form.materialeAttrezzature}
                  onChange={handleChange}
                  className={errors.materialeAttrezzature ? "error" : ""}
                  placeholder="Inserisci materiale e attrezzature"
                />
              ) : (
                <span className="edit-moduli-modern-text">
                  {modulo.materialeAttrezzature}
                </span>
              )}
              {errors.materialeAttrezzature && (
                <span className="edit-moduli-modern-error">
                  <i className="fa-solid fa-exclamation-triangle"></i>
                  {errors.materialeAttrezzature}
                </span>
              )}
            </div>
            <div className="edit-moduli-modern-field">
              <label className="edit-moduli-modern-label">
                <i className="fa-solid fa-book"></i>
                Dispense previste
              </label>
              {!editMode ? (
                <span className="edit-moduli-modern-text">
                  {getLabel(previsteDispenseOptions, modulo.previsteDispense)}
                </span>
              ) : (
                <CustomSelect
                  options={previsteDispenseOptions}
                  value={
                    form.previsteDispense
                      ? {
                          value: form.previsteDispense,
                          label: getLabel(
                            previsteDispenseOptions,
                            form.previsteDispense
                          ),
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange({
                      target: {
                        name: "previsteDispense",
                        value: selected ? selected.value : "",
                      },
                    })
                  }
                  placeholder="Seleziona dispense previste"
                />
              )}
              {errors.previsteDispense && (
                <span className="edit-moduli-modern-error">
                  <i className="fa-solid fa-exclamation-triangle"></i>
                  {errors.previsteDispense}
                </span>
              )}
            </div>
            <div className="edit-moduli-modern-field">
              <label className="edit-moduli-modern-label">
                <i className="fa-solid fa-graduation-cap"></i>
                Prove Finali
              </label>
              {!editMode ? (
                <span className="edit-moduli-modern-text">
                  {getLabel(proveFinaliOptions, modulo.proveFinali)}
                </span>
              ) : (
                <CustomSelect
                  options={proveFinaliOptions}
                  value={
                    form.proveFinali
                      ? {
                          value: form.proveFinali,
                          label: getLabel(proveFinaliOptions, form.proveFinali),
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange({
                      target: {
                        name: "proveFinali",
                        value: selected ? selected.value : "",
                      },
                    })
                  }
                  placeholder="Seleziona prove finali"
                />
              )}
              {errors.proveFinali && (
                <span className="edit-moduli-modern-error">
                  <i className="fa-solid fa-exclamation-triangle"></i>
                  {errors.proveFinali}
                </span>
              )}
            </div>
            <div className="edit-moduli-modern-field">
              <label className="edit-moduli-modern-label">
                <i className="fa-solid fa-align-left"></i>
                Descrizione
              </label>
              {editMode ? (
                <textarea
                  id="descrizione"
                  name="descrizione"
                  value={form.descrizione}
                  onChange={handleChange}
                  className={errors.descrizione ? "error" : ""}
                  placeholder="Inserisci la descrizione del modulo"
                  rows="4"
                />
              ) : (
                <span className="edit-moduli-modern-text">
                  {modulo.descrizione || "-"}
                </span>
              )}
              {errors.descrizione && (
                <span className="edit-moduli-modern-error">
                  <i className="fa-solid fa-exclamation-triangle"></i>
                  {errors.descrizione}
                </span>
              )}
            </div>
          </div>
          {canEdit && editMode && (
            <div className="edit-moduli-modern-actions">
              <button
                type="button"
                className="edit-moduli-modern-cancel"
                onClick={handleCancel}
              >
                <i className="fa-solid fa-times"></i>
                Annulla
              </button>
              <button type="submit" className="edit-moduli-modern-save">
                <i className="fa-solid fa-save"></i>
                Salva Modifica
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditModuliProtocollati;
